import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { getDefaultLocationColorResolver, hexToRgb, rgbToHex, tintRgb, type LocationColorFn } from '@/lib/locationColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export type ReportLang = 'no' | 'en';
export type ReportFormat = 'print' | 'pdf' | 'xlsx' | 'pdf-base64';

export interface PdfPayload { filename: string; base64: string }

interface PassengerListPrintOpts {
  tripNumbers: string[];
  weeklyPlan: any;
  lang?: ReportLang;
  format?: ReportFormat;
  /** Iter 798: ferdig hentede bookinger per tur (offentlig sjåførlenke / Passasjerlister-fanen) — hopper over API-kallet. */
  bookingsByTrip?: Record<string, any[]>;
  /** Iter 829: stedsfarge som i bookinglisten (forkortelser m/ farge). Utelatt + innlogget → hentes fra /api/colors/all. */
  locColor?: LocationColorFn;
}

// Ordbok for passasjerlisten. Norsk brukes internt, engelsk sendes til kunde.
const PL_LABELS: Record<ReportLang, Record<string, string>> = {
  no: {
    docTitle: 'Passasjerlister',
    route: 'RUTE',
    date: 'Dato', week: 'Uke', day: 'Dag', totPax: 'Tot. Pax', countedPax: 'Opptalt pax', driver: 'Sjåfør', vehicle: 'Kjøretøy', comment: 'Kommentar',
    from: 'Fra', finalStop: 'Endestopp', company: 'Selskap',
    stopWord: 'Stopp',
    flight: 'Flynr.', flightTime: 'Flytid',
    passengerList: 'Passasjerliste',
    checkOff: 'Avhuk.', number: 'Nr.', firstName: 'Fornavn', lastName: 'Etternavn', stop: 'Stopp',
    empty: 'Ingen passasjerer registrert.',
    paxWarning: 'Flere navn enn Tot. Pax – kontroller',
    names: 'navn',
    tripsWord: 'turer', tripWord: 'Tur',
    printBtn: 'Skriv ut',
    popupBlocked: 'Kunne ikke åpne utskriftsvinduet — tillat popups fra denne siden og prøv igjen.',
  },
  en: {
    docTitle: 'Passenger lists',
    route: 'ROUTE',
    date: 'Date', week: 'Week', day: 'Day', totPax: 'Tot. Pax', countedPax: 'Counted Pax', driver: 'Driver', vehicle: 'Vehicle', comment: 'Comment',
    from: 'From', finalStop: 'Final stop', company: 'Company',
    stopWord: 'Stop',
    flight: 'Flight', flightTime: 'Flight time',
    passengerList: 'Passenger list',
    checkOff: 'Check', number: 'Number', firstName: 'First Name', lastName: 'Last Name', stop: 'Stop',
    empty: 'No passengers registered.',
    paxWarning: 'More names than Tot. Pax – please check',
    names: 'names',
    tripsWord: 'trips', tripWord: 'Trip',
    printBtn: 'Print',
    popupBlocked: 'Could not open the print window — please allow popups from this page and try again.',
  },
};

const esc = (s: any) => String(s ?? '').replace(/[&<>"']/g, (c: string) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);

/** Bookingens flynummer (eget felt, ellers arrival_flights) — arves av passasjerer uten eget fly. */
export function bookingFlight(b: any): string {
  const own = String(b?.flight_number || '').trim();
  if (own) return own;
  const nums = (Array.isArray(b?.arrival_flights) ? b.arrival_flights : []).map((f: any) => String(f?.flight_number || '').trim()).filter(Boolean);
  return Array.from(new Set(nums)).join(', ');
}

/** Names come from the single linked passenger list (by-trip attaches `passengers`/`passenger_names`). */
function bookingNamedRows(b: any): { name: string; checked: boolean; flight: string }[] {
  const inherited = bookingFlight(b);
  const fromObjects = (Array.isArray(b.passengers) ? b.passengers : [])
    .map((p: any) => ({ name: String((p && (p.name || p.full_name)) || '').trim(), checked: !!(p && p.checked_in), flight: String((p && p.flight) || '').trim() || inherited }))
    .filter((r: any) => r.name);
  if (fromObjects.length) return fromObjects;
  return (Array.isArray(b.passenger_names) ? b.passenger_names : []).map((n: any) => ({ name: String(n || '').trim(), checked: false, flight: inherited })).filter((r: any) => r.name);
}

export interface PaxRow { idx: number; first: string; last: string; blank: boolean; checked: boolean; stop: string; company: string; flight: string }

/** Rows for one booking: every known name, then blank numbered rows up to the pax count — never «ukjent». */
function bookingPaxRows(b: any): Omit<PaxRow, 'idx' | 'stop' | 'company'>[] {
  const rows = bookingNamedRows(b).map((r) => {
    const parts = r.name.split(/\s+/);
    return { first: parts[0] || '', last: parts.slice(1).join(' '), blank: false, checked: r.checked, flight: r.flight };
  });
  const pax = Number(b.number_of_passengers) || 0;
  const checkedSeats: number[] = Array.isArray(b.checked_seat_numbers) ? b.checked_seat_numbers : [];
  const inherited = bookingFlight(b);
  for (let i = rows.length; i < pax; i++) rows.push({ first: '', last: '', blank: true, checked: checkedSeats.includes(i + 1), flight: inherited });
  return rows;
}

/** Alle fly på turen m/ Flytid — aldri tak. `trip.flights` (objekter) først, ellers rene flynummer. */
export function tripFlights(trip: any): { no: string; time: string }[] {
  const objs = (Array.isArray(trip?.flights) ? trip.flights : []).map((f: any) => ({ no: String(f?.flight_number || '').trim(), time: String(f?.arrival_time || '').trim() })).filter((f: any) => f.no);
  if (objs.length) return objs;
  return (Array.isArray(trip?.flight_numbers) ? trip.flight_numbers : []).map((n: any) => ({ no: String(n || '').trim(), time: '' })).filter((f: any) => f.no);
}

const BLANK_LINE = '______________';

/** Day name in the report language (the API returns Norwegian `day_name`). */
function localizedDayName(date: any, fallback: any, lang: ReportLang): string {
  const d = new Date(`${String(date || '').slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(fallback || '');
  const name = d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { weekday: 'long' });
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Filesystem-safe filename: {YYYY-MM-DD}_{HH-MM}_{Fra}-{Til}_{Sjåfør}.pdf (segments omitted when missing). */
function safeSegment(s: any): string {
  return String(s ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function passengerListFilename(trip: any, day: any, ext = 'pdf'): string {
  const date = String(day?.date || trip?.departure_date || '').slice(0, 10);
  const time = String(trip?.departure_time || '').replace(/[.:]/g, '-').slice(0, 5);
  const route = [safeSegment(trip?.from_location), safeSegment(trip?.to_location)].filter(Boolean).join('-');
  const driver = safeSegment(trip?.external_company || trip?.driver_name || '');
  const base = [date, time, route, driver].filter(Boolean).join('_') || `passasjerliste_${safeSegment(trip?.trip_number) || 'tur'}`;
  return `${base}.${ext}`;
}

function multiTripFilename(trips: any[], days: any[], L: Record<string, string>, wkNo: any, ext = 'pdf'): string {
  const dates = trips.map((t, i) => String(days[i]?.date || t?.departure_date || '').slice(0, 10)).filter(Boolean).sort();
  const span = dates.length ? (dates[0] === dates[dates.length - 1] ? dates[0] : `${dates[0]}_${dates[dates.length - 1]}`) : '';
  return `${[span, L.docTitle.toLowerCase().replace(/\s+/g, '-'), `${trips.length}-${L.tripsWord}`, wkNo ? `${L.week.toLowerCase()}-${wkNo}` : ''].filter(Boolean).join('_')}.${ext}`;
}

/** Everything one trip section needs — shared by print/PDF/Excel so the three formats never drift apart. */
export function tripContext(trip: any, day: any, bookings: any[], L: Record<string, string>) {
  const stops = trip.stops || [];
  // Dynamiske stopp-kolonner: minst 3 (gammel mal), flere når turen har flere stopp — ingen stopp droppes.
  const stopCols = Array.from({ length: Math.max(3, stops.length) }, (_, i) => ({
    label: `${L.stopWord} ${i + 1}`,
    loc: stops[i]?.location || '',
    time: stops[i]?.time || '',
  }));
  const companiesAll = Array.from(new Set(bookings.map((b: any) => b.company_name || b.customer_name).filter(Boolean)));
  const driverName = trip.external_company || trip.driver_name || '';
  const vehicle = trip.vehicle || trip.vehicle_name || trip.vehicle_registration || '';
  const flights = tripFlights(trip);
  const paxRows: PaxRow[] = [];
  const mismatch: { bn: string; names: number; pax: number }[] = [];
  let pIdx = 1;
  let sumPax = 0;
  bookings.forEach((b: any) => {
    const rows = bookingPaxRows(b);
    const named = rows.filter((r) => !r.blank).length;
    const pax = Number(b.number_of_passengers) || 0;
    sumPax += Math.max(pax, named);
    if (named > pax) mismatch.push({ bn: b.booking_number || b.id || '', names: named, pax });
    rows.forEach((r) => {
      paxRows.push({ idx: pIdx++, ...r, stop: b.from_location || trip.from_location || '', company: b.company_name || b.customer_name || '' });
    });
  });
  const totPax = Number(trip.total_passengers ?? sumPax) || sumPax || paxRows.length;
  const warning = mismatch.length
    ? `${L.paxWarning}: ${mismatch.map((m) => `${m.bn} (${m.names} ${L.names} / ${m.pax} pax)`).join(', ')}`
    : '';
  return { stopCols, companiesAll, driverName, vehicle, flights, paxRows, totPax, mismatch, warning };
}

/**
 * Åpner et utskriftbart dokument med én passasjerliste per valgt tur.
 * Layout følger den gamle Excel-malen (sjåførformat):
 *   • Toppseksjon: Rute (Dato/Uke/Dag/Tot. Pax/Opptalt pax/Sjåfør/Kjøretøy/Kommentar)
 *   • Stopp-rad: Fra / Stopp 1..N / Endestopp / Selskap (med klokkeslett)
 *   • Flynr. / Flytid — ett fly per rad, aldri tak
 *   • Passasjerliste: ☐ (avhukning) · Nr · Fornavn · Etternavn · Stopp · Flynr · Selskap
 * Sidebrytninger mellom hver tur. format 'xlsx' gir samme oppsett som regneark (ett ark per tur).
 */
export async function openPassengerListPrint({ tripNumbers: requestedTripNumbers, weeklyPlan, lang = 'no', format = 'print', bookingsByTrip: presetBookings, locColor: presetLocColor }: PassengerListPrintOpts) {
  if (!requestedTripNumbers.length) return;
  const L = PL_LABELS[lang];
  // Stoppfarger som i bookinglisten: eksplisitt fra kalleren (offentlig lenke) eller fra /api/colors/all (innlogget).
  const locColor: LocationColorFn = presetLocColor || (await getDefaultLocationColorResolver());

  const daysMap: Record<string, any> = {};
  const tripMap: Record<string, any> = {};
  (weeklyPlan?.days || []).forEach((day: any) => {
    (day.trips || []).forEach((t: any) => {
      tripMap[t.trip_number] = t;
      daysMap[t.trip_number] = day;
    });
  });

  // Seksjoner/ark i kronologisk rekkefølge (dato + avgang), ikke i den rekkefølgen turene ble huket av.
  const sortKey = (tn: string) => `${String(daysMap[tn]?.date || tripMap[tn]?.departure_date || '').slice(0, 10)} ${String(tripMap[tn]?.departure_time || '')} ${tn}`;
  const tripNumbers = [...requestedTripNumbers].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  const wkNo = weeklyPlan?.week_number || '';
  const dayName = (day: any) => localizedDayName(day?.date, day?.day_name, lang);

  // Fetch bookings-per-trip for all selected trips in parallel (unless the caller already has them — public driver link / tab).
  const bookingsByTrip: Record<string, any[]> = {};
  await Promise.all(tripNumbers.map(async (tn) => {
    if (presetBookings) { bookingsByTrip[tn] = presetBookings[tn] || []; return; }
    try {
      const res = await axios.get(`${API}/bookings/by-trip/${encodeURIComponent(tn)}`);
      const bookings = Array.isArray(res.data) ? res.data : (res.data?.bookings || []);
      bookingsByTrip[tn] = bookings;
    } catch (e) {
      bookingsByTrip[tn] = [];
    }
  }));

  const validTrips = tripNumbers.map((tn) => tripMap[tn]).filter(Boolean);
  const validDays = tripNumbers.filter((tn) => tripMap[tn]).map((tn) => daysMap[tn]);
  const fileFor = (ext: string) => (validTrips.length === 1
    ? passengerListFilename(validTrips[0], validDays[0], ext)
    : multiTripFilename(validTrips, validDays, L, wkNo, ext));

  // ---- Excel-eksport (sjåførformat, ett ark per tur; ExcelJS for cellefarger på stopp) ----
  if (format === 'xlsx') {
    const wb = new ExcelJS.Workbook();
    const usedNames = new Set<string>();
    const paintLoc = (cell: ExcelJS.Cell, loc: string) => {
      const color = loc ? locColor(loc) : null;
      const tint = color ? tintRgb(color) : null;
      if (!color || !tint) return;
      const argb = (hex: string) => `FF${hex.replace('#', '').toUpperCase()}`;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(rgbToHex(tint)) } };
      cell.border = { ...(cell.border || {}), left: { style: 'medium', color: { argb: argb(color) } } };
    };
    tripNumbers.forEach((tn) => {
      const trip = tripMap[tn];
      if (!trip) return;
      const day = daysMap[tn];
      const c = tripContext(trip, day, bookingsByTrip[tn] || [], L);
      const aoa: any[][] = [
        [L.route],
        [L.date, L.week, L.day, L.totPax, L.countedPax, L.driver, L.vehicle, L.comment],
        [String(day?.date || trip.departure_date || ''), wkNo, dayName(day), c.totPax, '', c.driverName, c.vehicle, trip.notes || ''],
        ...(c.warning ? [[c.warning]] : []),
        [],
        [L.from, ...c.stopCols.map((s) => s.label), L.finalStop, L.company],
        [trip.from_location || '', ...c.stopCols.map((s) => s.loc), trip.to_location || '', c.companiesAll.join(', ')],
        [trip.departure_time || '', ...c.stopCols.map((s) => s.time), '', ''],
        [],
        [L.flight, L.flightTime],
        ...(c.flights.length ? c.flights.map((f) => [f.no, f.time]) : [['', '']]),
        [],
        [L.passengerList],
        [L.checkOff, L.number, L.firstName, L.lastName, L.stop, L.flight, L.company],
        ...c.paxRows.map((r) => [false, r.idx, r.blank ? '' : r.first, r.blank ? '' : r.last, r.stop, r.flight, r.company]),
      ];
      let name = String(tn).replace(/[\\/?*[\]:]/g, '-').slice(0, 31) || 'Tur';
      while (usedNames.has(name)) name = `${name.slice(0, 28)}-${usedNames.size}`;
      usedNames.add(name);
      const ws = wb.addWorksheet(name);
      aoa.forEach((row) => ws.addRow(row));
      ws.columns = [8, 8, 18, 22, 18, 14, 22, 24].map((wch) => ({ width: wch })) as any;
      // Rute-raden (Fra / Stopp 1..N / Endestopp) og passasjerenes Stopp-kolonne får stedets farge.
      const routeRowIdx = aoa.findIndex((r) => r[0] === L.from && r[r.length - 1] === L.company) + 2;  // 1-basert rad etter overskriften «Fra …»
      const routeRow = ws.getRow(routeRowIdx);
      [trip.from_location || '', ...c.stopCols.map((s) => s.loc), trip.to_location || ''].forEach((loc, i) => paintLoc(routeRow.getCell(i + 1), loc));
      const paxHeaderIdx = aoa.findIndex((r) => r[0] === L.checkOff && r[1] === L.number) + 1;
      c.paxRows.forEach((r, i) => paintLoc(ws.getRow(paxHeaderIdx + 1 + i).getCell(5), r.stop));
    });
    if (!wb.worksheets.length) return;
    const filename = fileFor('xlsx');
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return filename;
  }

  const sections = tripNumbers.map((tn, idx) => {
    const trip = tripMap[tn];
    const day = daysMap[tn];
    if (!trip) return '';
    const c = tripContext(trip, day, bookingsByTrip[tn] || [], L);

    const locTd = (loc: string, extraClass = '') => {
      const color = loc ? locColor(loc) : null;
      const valid = !!color && !!hexToRgb(color);
      const style = valid ? ` style="background:${color}30;border-left:3px solid ${color}"` : '';
      const cls = [extraClass, valid ? 'loc' : ''].filter(Boolean).join(' ');
      return `<td${cls ? ` class="${cls}"` : ''}${style}${valid ? ` data-color="${color}"` : ''}>${esc(loc)}</td>`;
    };

    const passengerRows = c.paxRows.map((r) => `
          <tr>
            <td class="chk"><span class="box" aria-label="${esc(L.checkOff)}"></span></td>
            <td class="mono num">${r.idx}</td>
            <td class="${r.blank ? 'blank' : ''}">${r.blank ? BLANK_LINE : esc(r.first)}</td>
            <td class="${r.blank ? 'blank' : ''}">${r.blank ? BLANK_LINE : esc(r.last)}</td>
            ${locTd(r.stop)}
            <td class="mono">${esc(r.flight)}</td>
            <td>${esc(r.company)}</td>
          </tr>`);

    const flightRows = (c.flights.length ? c.flights : [{ no: '', time: '' }]).map((f) => `
              <tr><td class="mono">${esc(f.no)}</td><td class="mono">${esc(f.time)}</td></tr>`).join('');

    return `
      <section class="trip-page" ${idx > 0 ? 'style="page-break-before: always;"' : ''}>
        <div class="header-block">
          <div class="route-title">${esc(L.route)}</div>
          <table class="meta">
            <thead>
              <tr><th>${esc(L.date)}</th><th>${esc(L.week)}</th><th>${esc(L.day)}</th><th>${esc(L.totPax)}</th><th class="counted">${esc(L.countedPax)}</th><th>${esc(L.driver)}</th><th>${esc(L.vehicle)}</th><th>${esc(L.comment)}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>${esc(day?.date || trip.departure_date || '')}</td>
                <td>${esc(wkNo)}</td>
                <td>${esc(dayName(day))}</td>
                <td class="mono">${esc(c.totPax)}</td>
                <td class="counted-cell" data-testid="pl-counted-pax"><span class="counter-box"></span></td>
                <td>${esc(c.driverName)}</td>
                <td>${esc(c.vehicle)}</td>
                <td>${esc(trip.notes || '')}</td>
              </tr>
            </tbody>
          </table>
          ${c.warning ? `<div class="pax-warning" data-testid="pl-pax-warning">⚠ ${esc(c.warning)}</div>` : ''}

          <table class="meta">
            <thead>
              <tr><th>${esc(L.from)}</th>${c.stopCols.map((s) => `<th>${esc(s.label)}</th>`).join('')}<th>${esc(L.finalStop)}</th><th>${esc(L.company)}</th></tr>
            </thead>
            <tbody>
              <tr>
                ${locTd(trip.from_location || '')}
                ${c.stopCols.map((s) => locTd(s.loc)).join('')}
                ${locTd(trip.to_location || '')}
                <td>${esc(c.companiesAll.join(', '))}</td>
              </tr>
              <tr>
                <td class="mono">${esc(trip.departure_time || '')}</td>
                ${c.stopCols.map((s) => `<td class="mono">${esc(s.time)}</td>`).join('')}
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <table class="meta flights">
            <thead><tr><th>${esc(L.flight)}</th><th>${esc(L.flightTime)}</th></tr></thead>
            <tbody>${flightRows}
            </tbody>
          </table>
        </div>

        <h3 class="pl-title">${esc(L.passengerList)}</h3>
        <table class="pax">
          <thead>
            <tr>
              <th class="chk" title="${esc(L.checkOff)}">☐</th>
              <th style="width:7%">${esc(L.number)}</th>
              <th>${esc(L.firstName)}</th>
              <th>${esc(L.lastName)}</th>
              <th>${esc(L.stop)}</th>
              <th>${esc(L.flight)}</th>
              <th>${esc(L.company)}</th>
            </tr>
          </thead>
          <tbody>
            ${passengerRows.join('') || `<tr><td colspan="7" class="muted">${esc(L.empty)}</td></tr>`}
          </tbody>
        </table>

        <div class="footer-line">${esc(L.tripWord)} ${esc(trip.trip_number)} · ${esc(c.driverName)} · ${esc(c.vehicle)}</div>
      </section>
    `;
  }).join('');

  // ---- PDF-eksport via jsPDF + autoTable ('pdf-base64' returnerer innholdet for sending til sjåfør) ----
  if (format === 'pdf' || format === 'pdf-base64') {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const head = { fillColor: [243, 244, 246] as [number, number, number], textColor: 20, fontStyle: 'bold' as const };
    const drawBox = (data: any, size: number) => {
      const x = data.cell.x + (data.cell.width - size) / 2;
      const y = data.cell.y + (data.cell.height - size) / 2;
      doc.setDrawColor(60);
      doc.setLineWidth(0.3);
      doc.rect(x, y, size, size);
    };
    // Stedsfarge (som LocationCell på skjerm): lys fyll i didParseCell + venstre kant i didDrawCell.
    const paintLocCell = (data: any, loc: string) => {
      const color = loc ? locColor(loc) : null;
      const tint = color ? tintRgb(color) : null;
      if (tint) data.cell.styles.fillColor = tint;
    };
    const drawLocEdge = (data: any, loc: string) => {
      const color = loc ? locColor(loc) : null;
      const rgb = color ? hexToRgb(color) : null;
      if (!rgb) return;
      doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
      doc.setLineWidth(0.9);
      doc.line(data.cell.x + 0.45, data.cell.y, data.cell.x + 0.45, data.cell.y + data.cell.height);
      doc.setDrawColor(60);
      doc.setLineWidth(0.3);
    };
    for (let i = 0; i < tripNumbers.length; i++) {
      const tn = tripNumbers[i];
      const trip = tripMap[tn];
      if (!trip) continue;
      const day = daysMap[tn];
      const c = tripContext(trip, day, bookingsByTrip[tn] || [], L);

      if (i > 0) doc.addPage();
      doc.setFontSize(16);
      doc.text(L.route, 14, 16);

      autoTable(doc, {
        startY: 20,
        head: [[L.date, L.week, L.day, L.totPax, L.countedPax, L.driver, L.vehicle, L.comment]],
        body: [[
          String(day?.date || trip.departure_date || ''),
          String(wkNo),
          dayName(day),
          String(c.totPax),
          '',
          c.driverName,
          c.vehicle,
          trip.notes || '',
        ]],
        styles: { fontSize: 9, cellPadding: 2, minCellHeight: 9 },
        headStyles: head,
        columnStyles: { 4: { cellWidth: 24 } },
        margin: { left: 14, right: 14 },
        // «Opptalt pax»: tom boks sjåføren fyller ut for hånd.
        didDrawCell: (data: any) => { if (data.section === 'body' && data.column.index === 4) drawBox(data, 6); },
      });

      let y = (doc as any).lastAutoTable.finalY + 4;
      if (c.warning) {
        doc.setFontSize(9);
        doc.setTextColor(180, 83, 9);
        doc.text(doc.splitTextToSize(`! ${c.warning}`, 182), 14, y);
        doc.setTextColor(20);
        y += 5 * Math.max(1, doc.splitTextToSize(`! ${c.warning}`, 182).length);
      }
      const routeLocs = [trip.from_location || '', ...c.stopCols.map((s) => s.loc), trip.to_location || ''];
      autoTable(doc, {
        startY: y,
        head: [[L.from, ...c.stopCols.map((s) => s.label), L.finalStop, L.company]],
        body: [
          [...routeLocs, c.companiesAll.join(', ')],
          [trip.departure_time || '', ...c.stopCols.map((s) => s.time), '', ''],
        ],
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { ...head, fontSize: c.stopCols.length > 3 ? 7.5 : 9 },
        margin: { left: 14, right: 14 },
        // Stoppfarger som i bookinglisten: lys fyllfarge + venstre kant i stedets farge.
        didParseCell: (data: any) => { if (data.section === 'body' && data.row.index === 0 && data.column.index < routeLocs.length) paintLocCell(data, routeLocs[data.column.index]); },
        didDrawCell: (data: any) => { if (data.section === 'body' && data.row.index === 0 && data.column.index < routeLocs.length) drawLocEdge(data, routeLocs[data.column.index]); },
      });

      y = (doc as any).lastAutoTable.finalY + 4;
      autoTable(doc, {
        startY: y,
        head: [[L.flight, L.flightTime]],
        body: c.flights.length ? c.flights.map((f) => [f.no, f.time]) : [['', '']],
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: head,
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 } },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 6;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(L.passengerList, 14, y);

      const paxBody: any[] = c.paxRows.map((r) => [
        '',
        String(r.idx),
        r.blank ? { content: BLANK_LINE, styles: { textColor: 160 } } : r.first,
        r.blank ? { content: BLANK_LINE, styles: { textColor: 160 } } : r.last,
        r.stop,
        r.flight,
        r.company,
      ]);

      autoTable(doc, {
        startY: y + 2,
        head: [['', L.number, L.firstName, L.lastName, L.stop, L.flight, L.company]],
        body: paxBody.length ? paxBody : [[{ content: L.empty, colSpan: 7, styles: { halign: 'center', textColor: 120, fontStyle: 'italic' } as any }]],
        styles: { fontSize: 9, cellPadding: 2, minCellHeight: 7 },
        headStyles: { fillColor: [238, 242, 255], textColor: 20, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 11, halign: 'right' }, 5: { cellWidth: 24 } },
        margin: { left: 14, right: 14 },
        // Avhukning: tom rute i overskriften og på hver passasjerrad. Stopp-kolonnen (4) farges som stedet.
        didParseCell: (data: any) => { if (data.section === 'body' && data.column.index === 4 && paxBody.length) paintLocCell(data, c.paxRows[data.row.index]?.stop || ''); },
        didDrawCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 4 && paxBody.length) drawLocEdge(data, c.paxRows[data.row.index]?.stop || '');
          if (data.column.index !== 0) return;
          if (data.section === 'head') drawBox(data, 3.2);
          else if (data.section === 'body' && paxBody.length) drawBox(data, 3.6);
        },
      });

      const bottomY = (doc as any).lastAutoTable.finalY + 5;
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.setFont('helvetica', 'normal');
      doc.text(`${L.tripWord} ${trip.trip_number} · ${c.driverName} · ${c.vehicle}`, 14, bottomY);
    }
    const filename = fileFor('pdf');
    if (format === 'pdf-base64') {
      const base64 = String(doc.output('datauristring')).split(',')[1] || '';
      return { filename, base64 } as PdfPayload;
    }
    doc.save(filename);
    return filename;
  }

  const printTitle = fileFor('pdf').replace(/\.pdf$/i, '');

  const html = `
    <!doctype html>
    <html lang="${lang}">
      <head>
        <meta charset="utf-8" />
        <title>${esc(printTitle)}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #111; margin: 0; padding: 16px; }
          .trip-page { }
          .route-title { font-size: 18px; font-weight: 700; margin: 0 0 4px 0; letter-spacing: 1px; }
          table.meta { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
          table.meta th, table.meta td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
          table.meta th { background: #f3f4f6; text-transform: uppercase; font-size: 10px; letter-spacing: 0.3px; }
          table.meta th.counted { background: #fef3c7; }
          table.meta.flights { width: auto; min-width: 40%; }
          .counted-cell { background: #fffbeb; text-align: center; }
          .counter-box { display: inline-block; width: 56px; height: 18px; border: 1.5px solid #333; border-radius: 2px; vertical-align: middle; }
          .pax-warning { font-size: 11px; color: #92400e; background: #fffbeb; border: 1px solid #f59e0b; border-radius: 4px; padding: 4px 8px; margin: 0 0 8px 0; }
          .pl-title { font-size: 13px; font-weight: 700; margin: 12px 0 4px 0; }
          table.pax { width: 100%; border-collapse: collapse; font-size: 11px; }
          table.pax th, table.pax td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
          table.pax th { background: #eef2ff; text-transform: uppercase; font-size: 10px; }
          table.pax .chk { width: 30px; text-align: center; }
          table.pax .num { width: 32px; text-align: right; }
          .box { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #333; border-radius: 2px; vertical-align: middle; }
          .mono { font-family: "SF Mono", Menlo, monospace; }
          .muted { color: #888; font-style: italic; }
          .blank { color: #aaa; letter-spacing: 1px; }
          .footer-line { margin-top: 12px; font-size: 10px; color: #666; border-top: 1px dashed #bbb; padding-top: 4px; }
          .print-btn { position: fixed; top: 8px; right: 8px; padding: 8px 12px; background: #6d28d9; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }
          @media print { .print-btn { display: none; } .box, .counter-box, .pax-warning, td.loc { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <button class="print-btn" onclick="window.print()">${esc(L.printBtn)}</button>
        ${sections}
      </body>
    </html>
  `;

  const w = window.open('', '_blank', 'width=1000,height=800');
  if (!w) {
    toast.error(L.popupBlocked);
    return;
  }
  w.document.write(html);
  w.document.close();
  setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } }, 500);
}
